import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

const DISCORD_URL =
  import.meta.env.VITE_DISCORD_URL || 'https://discord.gg/R4udP6TbM2'

const NAV_ITEMS: Array<
  | { to: string; label: string }
  | { href: string; label: string; external?: boolean }
> = [
  { href: '/#how', label: 'how' },
  { to: '/pricing', label: 'pricing' },
  { to: '/roadmap', label: 'roadmap' },
]

function LogoMark() {
  return (
    <span className="od-logo-mark" aria-hidden="true">
      <svg viewBox="0 0 180 90" focusable="false">
        <circle cx="44" cy="45" r="24" stroke="currentColor" strokeWidth="14" fill="none" />
        <path d="M86 68L112 22" stroke="currentColor" strokeWidth="14" strokeLinecap="square" />
      </svg>
    </span>
  )
}

function DiscordIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"
        fill="currentColor"
      />
    </svg>
  )
}

export default function SiteNav() {
  const location = useLocation()
  const { getDashboardUrl } = useAuth()
  const isHowActive = location.pathname === '/' && location.hash === '#how'
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname, location.hash])

  useEffect(() => {
    if (!menuOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    const onClickOutside = (e: MouseEvent) => {
      const t = e.target as Element | null
      if (!t) return
      if (t.closest('.od-nav')) return
      setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('click', onClickOutside)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('click', onClickOutside)
    }
  }, [menuOpen])

  return (
    <header className={`od-nav${menuOpen ? ' is-menu-open' : ''}`}>
      <Link to="/" className="od-brand" aria-label="opendeploy home">
        <LogoMark />
        <span>opendeploy</span>
      </Link>

      <nav aria-label="Primary" id="od-nav-menu">
        {NAV_ITEMS.map((item) =>
          'href' in item ? (
            <a
              key={item.href}
              href={item.href}
              className={item.href === '/#how' && isHowActive ? 'is-active' : undefined}
              target={item.external ? '_blank' : undefined}
              rel={item.external ? 'noopener noreferrer' : undefined}
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </a>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? 'is-active' : undefined)}
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </NavLink>
          ),
        )}
      </nav>

      <button
        type="button"
        className="od-nav-hamburger"
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        aria-controls="od-nav-menu"
        onClick={() => setMenuOpen((v) => !v)}
      >
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </button>

      <a
        className="od-nav-discord"
        href={DISCORD_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Join the OpenDeploy Discord"
        onClick={() => setMenuOpen(false)}
      >
        <DiscordIcon />
      </a>

      <a
        className="od-nav-dashboard"
        href={getDashboardUrl()}
        aria-label="Open opendeploy dashboard"
        onClick={() => setMenuOpen(false)}
      >
        dashboard
      </a>
    </header>
  )
}
